import http from '@services/http'
import {
  IUserPickCreate,
  IUserPickLikeToggle,
  IUserPickHomePackage,
  IUserPickMatchDetailPackage,
  IUserPickMatchPackage,
  IUserPickSpecialMember,
  IUserPickLeagueFilter,
  IUserPickMatches,
  IUserPickFilteredMatch,
  IUserPickSpecificUsersMatches,
  IUserPickSpecificMemberPackage,
  IUserPickList,
  IUserPickSpecificMemberPick,
  IUserPickMonthlyBestUserPick, IUserPickLastBestUserPick,
  IUserPickMatch,
} from '@protocol/user-pick'

class UserPickApi {

  /**
   * 나도 승부사 홈에서 매치 목록 및 경기별 분석글 매치 개수 반환
   * @param params
   */
  async fetchHomePackage(params: IUserPickHomePackage.Params): Promise<IUserPickHomePackage.Schema> {
    return http.get('userPick/homePackage', params)
  }

  /**
   * 나도 승부사 매치목록, S맴버목록, 리그필터 목록을 가지고 온다.
   * @param params
   */
  async fetchMatchPackage(params: IUserPickMatchPackage.Params): Promise<IUserPickMatchPackage.Schema> {
    return http.get('userPick/matchPackage', params)
  }

  /**
   * 나도 승부사 분석글 매치 정보를 (단일) 가지고 온다.
   * @param params
   */
  async fetchMatch(params: IUserPickMatch.Params): Promise<IUserPickMatch.Schema> {
    return http.get('userPick/match', params)
  }

  /**
   * 나도 승부사 분석글 매치 목록을 가지고 온다.
   *
   * 페이징용
   *
   * @param params
   */
  async fetchMatches(params: IUserPickMatches.Params): Promise<IUserPickMatches.Schema> {
    return http.get('userPick/matches', params)
  }

  /**
   * 나도 승부사 리그 필터 적용된 매치 목록을 가지고 온다.
   * @param params
   */
  async fetchFilteredMatches(params: IUserPickFilteredMatch.Params): Promise<IUserPickFilteredMatch.Schema> {
    return http.get('userPick/filteredMatches', params)
  }

  /**
   * 나도 승부사 S멤버십 목록을 가지고 온다.
   * @param params
   */
  async fetchSpecialMembers(params: IUserPickSpecialMember.Params): Promise<IUserPickSpecialMember.Schema> {
    return http.get('userPick/sMembers', params)
  }

  /**
   * 나도 승부사 리그 필터 목록을 가지고 온다.
   * @param params
   */
  async fetchLeagueFilters(params: IUserPickLeagueFilter.Params): Promise<IUserPickLeagueFilter.Schema> {
    return http.get('userPick/leagueFilters', params)
  }

  /**
   * 나도 승부사 분석글 추가 내용 가지고 오기
   *
   * 매치 정보, 승무패 베팅 정보, 분석글 목록 패키지를 가지고 온다.
   *
   * @param params
   */
  async fetchUserPickMatchDetailPackage(
    params: IUserPickMatchDetailPackage.Params
  ): Promise<IUserPickMatchDetailPackage.Schema> {
    return http.get('userPick/matchDetailPackage', params || {})
  }

  /**
   * 분석글 등록
   * @param params
   */
  async createUserPick(params: IUserPickCreate.Params): Promise<IUserPickCreate.Schema> {
    return http.post('userPick/create', params)
  }

  /**
   * 분석글 좋아요 토글
   * @param params
   */
  async toggleUserPickLike(params: IUserPickLikeToggle.Params): Promise<IUserPickLikeToggle.Schema> {
    return http.post('userPick/toggleLike', params)
  }

  /**
   * 나도 승부사 페이지에서 특정 멤버들을 선택시 해당 멤버의 분석글 매치들을 가지고 온다.
   * @param params
   */
  async fetchSpecificUsersMatches(
    params: IUserPickSpecificUsersMatches.Params
  ): Promise<IUserPickSpecificUsersMatches.Schema> {
    return http.get('userPick/specificUsersMatches', params)
  }

  /**
   * 특정 유저의 최근 마감 10경기 결과 목록과
   *
   * 작성한 모든 분석글을 가지고 온다.
   *
   * @param params
   */
  async fetchSpecificUserPackage(
    params: IUserPickSpecificMemberPackage.Params
  ): Promise<IUserPickSpecificMemberPackage.Schema> {
    return http.get('userPick/specificUserPackage', params)
  }

  /**
   * 특정 유저의 분석글들을 반환한다.
   *
   * 페이징용
   *
   * @param params
   */
  async fetchSpecificMemberPicks(
    params: IUserPickSpecificMemberPick.Params
  ): Promise<IUserPickSpecificMemberPick.Schema> {
    return http.get('userPick/specificMemberPicks', params)
  }

  /**
   * 특정 매치의 분석글 목록을 가지고 온다.
   *
   * 페이징용
   *
   * @param params
   */
  async fetchUserPicks(params: IUserPickList.Params): Promise<IUserPickList.Schema> {
    return http.get('userPick/list', params)
  }

  /**
   * 특정 월의 모든 오늘의 승부사 분석글을 반환 한다.
   *
   * @param params
   */
  async fetchMonthlyBestUserPicks(
    params: IUserPickMonthlyBestUserPick.Params
  ): Promise<IUserPickMonthlyBestUserPick.Schema> {
    return http.get('userPick/monthlyBestUserPicks', params)
  }

  /**
   * 채팅창 상단 오늘의 승부사 뱃지 출력 여부 판단을 위해 마지막 오늘의 승부사 분석글을 가지고 온다. 판단 기준은 userPick.updatedAt desc
   *
   * 유저가 오늘의 승부사 모아보기 팝업 오픈시 마지막 오늘의 승부사 분석글의 아이디를 로컬 스토리지에 저장 후 비교 하여 판단.
   *
   * @param params
   */
  async fetchLastBestUserPickId(
    params?: IUserPickLastBestUserPick.Params
  ): Promise<IUserPickLastBestUserPick.Schema> {
    return http.get('userPick/lastBestUserPickId', params)
  }
}

export default new UserPickApi()
